// v0.40 — app appearance. The chosen theme is a per-device preference
// (localStorage, like m.hasWallet), applied to <html data-theme> so the whole
// token palette switches (theme-dark.css / theme-pure.css).
// v0.60.7 (S9): Pure (light) is the default.
// 1.4.37 (Alexander, 17 Oct): Dusk (dark) is the default — Pure is betterthan.cash's, the
// landing; every screen at globalmoneyapp.com is Dusk until the person chooses otherwise.
// v0.60.17: three-way preference — "light" (Pure) | "dark" (Midnight) | "auto"
// (follow the system: prefers-color-scheme, which iOS/macOS drive by their own
// sunset/sunrise or schedule). The stored value is the PREFERENCE; the
// <html data-theme> attribute is always the RESOLVED theme ("light"/"dark") so
// the CSS never has to know about "auto". Ambient-light sensing isn't available
// to web apps on iOS, so "follow the system" is the honest version of it.
//
// First paint is handled by a tiny inline script in index.html (same rules,
// runs before CSS loads, so there's no flash); this module is the runtime path.

export type Theme = "dark" | "light";
export type ThemePref = Theme | "auto";

const KEY = "m.theme";
const THEME_COLOR: Record<Theme, string> = {
  dark: "#010f33", // 1.5.19: Midnight Cobalt (1.4.12–1.5.17: Dusk #0f1a33; before that #08183a)
  light: "#fbfaf8",
};
const MQ = "(prefers-color-scheme: dark)";

export function getThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "auto" ? v : "dark";
  } catch {
    return "dark";
  }
}

function systemTheme(): Theme {
  try {
    return window.matchMedia?.(MQ).matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function resolveTheme(pref: ThemePref = getThemePref()): Theme {
  return pref === "auto" ? systemTheme() : pref;
}

/** The theme currently on screen (resolved). */
export function getTheme(): Theme {
  const t = document.documentElement.dataset.theme;
  return t === "dark" ? "dark" : "light";
}

// 1.4.19 (Alexander, 16 Oct) — Global mode (the globe lit, the Swap screen with it) wears the original
// 1.5.19 (Alexander, 26 Oct) — one Midnight: the `html.midnight` switch of 1.4.19 (Global mode wore the old electric
// navy) is gone; Global and Swap take the same Cobalt as the rest.
function syncMeta(t: Theme): void {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", THEME_COLOR[t]);
}

function apply(t: Theme): void {
  document.documentElement.dataset.theme = t;
  syncMeta(t);
}

let _mqBound = false;
function bindSystemListener(): void {
  if (_mqBound) return;
  _mqBound = true;
  try {
    window.matchMedia?.(MQ).addEventListener("change", () => {
      if (getThemePref() === "auto" && !screenForced) apply(systemTheme());
    });
  } catch {
    /* no matchMedia — auto degrades to light */
  }
}

export function setTheme(pref: ThemePref): void {
  void import("./settings").then((m) => m.touch("theme")); // v0.80.40 — wins the next merge
  try {
    localStorage.setItem(KEY, pref);
  } catch {
    /* private mode / storage blocked — still apply for this session */
  }
  screenForced = null;
  apply(resolveTheme(pref));
  if (pref === "auto") bindSystemListener();
}

// 1.4.39 (Alexander, 18 Oct) — the screens a first-time visitor meets wear Pure, whatever the
// preference: the landing at globalmoneyapp.com (no wallet in this browser yet), the code screen
// and the name before Face ID — the same face as betterthan.cash. Everyone with a passkey here
// (the login), and a note's Take page or a payment request, keep the person's theme (Dusk unless
// chosen otherwise). A screen's theme is never stored; null hands the screen back to the preference.
let screenForced: Theme | null = null;
export function setScreenTheme(t: Theme | null): void {
  if (t === screenForced) return;
  screenForced = t;
  apply(t ?? resolveTheme());
}

/** 1.4.72 — the inline script's first-visit rule (index.html), read the same way: the landing, /join and an invite
 *  in a browser with no wallet, not the installed app. initTheme had re-applied the preference over it (Dusk),
 *  so /join showed Dusk for the moment between the boot and the code screen (Alexander's flicker, 21 Oct). */
function firstVisit(): boolean {
  try {
    const p = location.pathname.toLowerCase();
    const standalone = !!window.matchMedia?.("(display-mode: standalone)").matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
    return (p === "/" || p === "/join" || p.startsWith("/invite/")) && localStorage.getItem("m.hasWallet") !== "1" && !standalone;
  } catch { return false; }
}

/** Call once at boot: re-applies (no-op if the inline script got it right) and arms the listener for Auto. */
export function initTheme(): void {
  if (screenForced === null && firstVisit()) screenForced = "light"; // 1.4.72 — as the inline script painted it
  apply(screenForced ?? resolveTheme());
  if (getThemePref() === "auto") bindSystemListener();
}
